<!-- Floating trigger button — pill when closed, circle when open -->
<button class="fab" [class.fab--open]="open()" (click)="toggle()">
  @if (open()) {
    <mat-icon>close</mat-icon>
  } @else {
    <span class="fab-ring fab-ring--1"></span>
    <span class="fab-ring fab-ring--2"></span>
    <mat-icon class="fab-icon">auto_awesome</mat-icon>
    <span class="fab-label">Ask Wave</span>
  }
</button>

<!-- Chat panel -->
@if (open()) {
  <div class="panel" [class.maximized]="maximized()" role="dialog" aria-label="Ask Wave">

    <!-- Header -->
    <div class="panel-header">
      <button class="icon-btn" (click)="toggleSidebar()" matTooltip="Toggle history">
        <mat-icon>menu</mat-icon>
      </button>
      <div class="header-brand">
        <div class="brand-icon"><mat-icon>auto_awesome</mat-icon></div>
        <span class="header-title">Ask Wave</span>
        <span class="beta-badge">Beta</span>
      </div>
      <div class="header-actions">
        <button class="icon-btn" (click)="newConversation()" matTooltip="New conversation">
          <mat-icon>add_comment</mat-icon>
        </button>
        <button class="icon-btn" (click)="toggleMaximize()" [matTooltip]="maximized() ? 'Restore' : 'Maximize'">
          <mat-icon>{{ maximized() ? 'close_fullscreen' : 'open_in_full' }}</mat-icon>
        </button>
        <button class="icon-btn" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <div class="panel-body">

      <!-- Sidebar: conversation history -->
      @if (sidebarVisible()) {
        <aside class="sidebar">
          <div class="sidebar-label">History</div>
          @for (conv of chat.conversations(); track conv.session_id) {
            <div
              class="conv-item"
              [class.active]="conv.session_id === chat.sessionId()"
              (click)="selectConversation(conv)"
            >
              @if (editingSessionId === conv.session_id) {
                <input
                  class="conv-title-input"
                  [id]="'title-input-' + conv.session_id"
                  [(ngModel)]="editingTitle"
                  (keydown)="onTitleKeydown($event, conv.session_id)"
                  (blur)="saveEdit(conv.session_id)"
                  (click)="$event.stopPropagation()"
                />
              } @else {
                <span class="conv-title">{{ conv.title }}</span>
                <button class="conv-action" (click)="startEdit($event, conv)" matTooltip="Rename">
                  <mat-icon>edit</mat-icon>
                </button>
                <button class="conv-action" (click)="deleteConversation($event, conv.session_id)" matTooltip="Delete">
                  <mat-icon>delete_outline</mat-icon>
                </button>
              }
            </div>
          }
          @if (chat.conversations().length === 0) {
            <div class="conv-empty">No past conversations</div>
          }
        </aside>
      }

      <!-- Message thread -->
      <div class="thread">
        <div class="message-list" #messageList>
          @if (chat.messages().length === 0) {
            <div class="welcome">
              <div class="welcome-brand-icon"><mat-icon>auto_awesome</mat-icon></div>
              <div class="welcome-title-row">
                <p class="welcome-name">Ask Wave</p>
                <span class="beta-badge">Beta</span>
              </div>
              <p class="welcome-sub">Ask me about your projects, team, or estimates.</p>
              <div class="prompt-chips">
                @for (p of suggestedPrompts; track p.text) {
                  <button class="prompt-chip" (click)="sendPrompt(p)" [disabled]="chat.streaming()">
                    <mat-icon>{{ p.icon }}</mat-icon>
                    {{ p.label }}
                  </button>
                }
              </div>
            </div>
          }

          @for (msg of chat.messages(); track msg.id) {
            <div class="message" [class.user]="msg.role === 'user'" [class.assistant]="msg.role === 'assistant'">
              @if (msg.role === 'assistant') {
                <div class="avatar bot-avatar"><mat-icon>auto_awesome</mat-icon></div>
              }
              <div class="bubble">
                @if (msg.role === 'assistant') {
                  @if (msg.partial && msg.content === '' && !msg.toolCalling) {
                    <div class="typing">
                      <span></span><span></span><span></span>
                    </div>
                  }
                  @if (msg.toolCalling) {
                    <div class="tool-chip">
                      <mat-icon>search</mat-icon>
                      Looking up {{ msg.toolCalling | titlecase }}…
                    </div>
                  }
                  @if (msg.content) {
                    <div class="markdown" [innerHTML]="renderMarkdown(msg.content)"></div>
                    @if (!msg.partial && hasTable(msg.content)) {
                      <button class="export-btn" (click)="exportCsv(msg.content, msg.id)" matTooltip="Export to CSV">
                        <mat-icon>download</mat-icon>
                        Export CSV
                      </button>
                    }
                  }
                  @if (msg.suggestionChips?.length) {
                    <div class="suggestion-chips">
                      @for (chip of msg.suggestionChips!; track chip) {
                        <button class="suggestion-chip" (click)="sendSuggestion(chip)" [disabled]="chat.streaming()">
                          {{ chip }}
                        </button>
                      }
                    </div>
                  }
                  @if (msg.formRequest) {
                    <app-chat-action-form
                      [formDef]="msg.formRequest"
                      [submitted]="!!msg.formSubmitted"
                      (formSubmit)="onFormSubmit($event, msg)"
                    ></app-chat-action-form>
                  }
                } @else {
                  <span class="user-text">{{ msg.content }}</span>
                }
              </div>
              @if (msg.role === 'user') {
                <div class="avatar user-avatar">
                  @if (chat.userProfilePicture) {
                    <img [src]="chat.userProfilePicture" [alt]="chat.userName" />
                  } @else {
                    <span class="avatar-initials">{{ chat.userName.charAt(0).toUpperCase() }}</span>
                  }
                </div>
              }
            </div>
          }
        </div>

        <!-- Input area -->
        <div class="input-area">
          <textarea
            class="msg-input"
            [(ngModel)]="inputText"
            (keydown)="onKeydown($event)"
            placeholder="Ask Wave anything about your projects…"
            rows="1"
            [disabled]="chat.streaming()"
          ></textarea>
          <button
            class="send-btn"
            (click)="send()"
            [disabled]="chat.streaming() || !inputText().trim()"
            matTooltip="Send"
          >
            <mat-icon>send</mat-icon>
          </button>
        </div>
      </div>

    </div>
  </div>
}
